import type { AuthProvider, CoreAdminProps } from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

import { Admin } from "@/components/admin/admin";
import { PaginaOlvido } from "../auth/PaginaOlvido";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { ConfirmationRequired } from "@/components/atomic-crm/login/ConfirmationRequired";
import { getAuthProvider } from "@/components/atomic-crm/providers/supabase";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "@/components/atomic-crm/root/ConfigurationContext";
import { defaultConfiguration } from "@/components/atomic-crm/root/defaultConfiguration";
import type { Sale } from "@/components/atomic-crm/types";
import { useIsMobile } from "@/hooks/use-mobile";

import { AgendaPage } from "../agenda/AgendaPage";
import { AjustesPage } from "../ajustes/AjustesPage";
import { PaginaPerfil } from "../ajustes/PaginaPerfil";
import { PaginaInicio } from "../auth/PaginaInicio";
import { RegistroInicial } from "../auth/RegistroInicial";
import {
  getNevoraDataProvider,
  type NevoraDataProvider,
} from "../datos/dataProvider";
import { i18nProvider } from "../i18n/i18nProvider";
import { ComisionesPage } from "../equipo/ComisionesPage";
import { MisComisionesPage } from "../equipo/MisComisionesPage";
import { EquipoPage } from "../equipo/EquipoPage";
import { ResumenPage } from "../resumen/ResumenPage";
import { InformeMensual } from "../panel/InformeMensual";
import { SinAsignarPage } from "../equipo/SinAsignarPage";
import { ColaboracionesPage } from "../equipo/ColaboracionesPage";
import { ImportarPage } from "../importar/ImportarPage";
import { LayoutEscritorio } from "../layout/LayoutEscritorio";
import { LayoutMovil } from "../layout/LayoutMovil";
import negocios from "../negocios";
import { Inicio } from "../panel/Inicio";
import { PipelinePage } from "../pipeline/PipelinePage";
import isotipo from "../tema/isotipo.png";
import isotipoBlanco from "../tema/isotipo-blanco.png";

const TITULO = "Nevora CRM";
const almacen = localStorageStore(undefined, "NevoraSales");

/**
 * Raíz de Nevora Sales. Copia adaptada de root/CRM.tsx de Atomic:
 *  - sin telemetría, sin contactos/empresas/deals/tareas/etiquetas;
 *  - recursos `negocios` y `notas`, rutas pipeline/agenda/importar/ajustes/perfil;
 *  - layouts, páginas de acceso, panel e i18n propios (src/nevora/);
 *  - mismos providers de Supabase que Atomic (auth + datos), misma alternancia
 *    escritorio/móvil a 768 px y misma caché persistente de React Query en móvil.
 */
export const NevoraCRM = () => {
  const dataProvider = useMemo<NevoraDataProvider>(
    () => getNevoraDataProvider(),
    [],
  );
  const authProvider = useMemo<AuthProvider>(() => getAuthProvider(), []);
  const isMobile = useIsMobile();

  // Título y logos que leen los componentes heredados de Atomic (ConfirmationRequired,
  // Set/ForgotPassword…) a través de useConfigurationContext. Se fija siempre.
  useEffect(() => {
    const actual =
      (almacen.getItem(CONFIGURATION_STORE_KEY) as
        | Partial<ConfigurationContextValue>
        | undefined) ?? {};
    almacen.setItem(CONFIGURATION_STORE_KEY, {
      ...defaultConfiguration,
      ...actual,
      title: TITULO,
      currency: "EUR",
      lightModeLogo: isotipo,
      darkModeLogo: isotipoBlanco,
    } satisfies ConfigurationContextValue);
  }, []);

  // Igual que Atomic: al iniciar sesión se precarga la configuración de la BD.
  const authProviderEnvuelto = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const resultado = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            almacen.setItem(CONFIGURATION_STORE_KEY, {
              ...config,
              title: TITULO,
              lightModeLogo: isotipo,
              darkModeLogo: isotipoBlanco,
            });
          }
        } catch {
          // no crítico
        }
        return resultado;
      },
      logout: async (params: any) => {
        try {
          almacen.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // ignorar
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider],
  );

  const AdminResponsive = isMobile ? AdminMovil : AdminEscritorio;

  return (
    <AdminResponsive
      dataProvider={dataProvider}
      authProvider={authProviderEnvuelto}
      i18nProvider={i18nProvider}
      store={almacen}
      loginPage={PaginaInicio}
      title={TITULO}
      requireAuth
      disableTelemetry
    />
  );
};

// ra-core solo reconoce <Resource> y <CustomRoutes> como HIJOS DIRECTOS de <Admin>
// (si van envueltos en un componente, enseña la página "Ready" de shadcn-admin-kit).
// Por eso las rutas se repiten en línea en los dos Admin, igual que hace Atomic.
const representarUsuario = (r: Sale) => `${r.first_name} ${r.last_name}`;

const AdminEscritorio = (props: CoreAdminProps) => (
  <Admin layout={LayoutEscritorio} dashboard={Inicio} {...props}>
    <CustomRoutes noLayout>
      <Route path={RegistroInicial.path} element={<RegistroInicial />} />
      <Route
        path={ConfirmationRequired.path}
        element={<ConfirmationRequired />}
      />
      <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
      <Route path={PaginaOlvido.path} element={<PaginaOlvido />} />
    </CustomRoutes>
    <CustomRoutes>
      <Route path={PaginaPerfil.path} element={<PaginaPerfil />} />
      <Route path={PipelinePage.path} element={<PipelinePage />} />
      <Route path="/agenda" element={<AgendaPage />} />
      <Route path={ImportarPage.path} element={<ImportarPage />} />
      <Route path={AjustesPage.path} element={<AjustesPage />} />
      <Route path={EquipoPage.path} element={<EquipoPage />} />
      <Route path={ResumenPage.path} element={<ResumenPage />} />
      <Route path={InformeMensual.path} element={<InformeMensual />} />
      <Route path={SinAsignarPage.path} element={<SinAsignarPage />} />
      <Route path={ColaboracionesPage.path} element={<ColaboracionesPage />} />
      <Route path={ComisionesPage.path} element={<ComisionesPage />} />
      <Route path={MisComisionesPage.path} element={<MisComisionesPage />} />
    </CustomRoutes>
    <Resource name="negocios" {...negocios} />
    <Resource name="notas" />
    <Resource name="avisos" />
    <Resource name="cobros" />
    {/* `sales` (usuarios) sin pantallas: recurso para referencias y perfil. Pantallas en FASE 4. */}
    <Resource name="sales" recordRepresentation={representarUsuario} />
  </Admin>
);

const AdminMovil = (props: CoreAdminProps) => {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            gcTime: 1000 * 60 * 60 * 24, // 24 h
            networkMode: "offlineFirst",
          },
          mutations: {
            networkMode: "offlineFirst",
          },
        },
      }),
    [],
  );
  const persistidor = useMemo(
    () => createAsyncStoragePersister({ storage: localStorage }),
    [],
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: persistidor }}
    >
      <Admin
        queryClient={queryClient}
        layout={LayoutMovil}
        dashboard={Inicio}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={RegistroInicial.path} element={<RegistroInicial />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route path={PaginaOlvido.path} element={<PaginaOlvido />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path={PaginaPerfil.path} element={<PaginaPerfil />} />
          <Route path={PipelinePage.path} element={<PipelinePage />} />
          <Route path="/agenda" element={<AgendaPage />} />
          <Route path={ImportarPage.path} element={<ImportarPage />} />
          <Route path={AjustesPage.path} element={<AjustesPage />} />
          <Route path={EquipoPage.path} element={<EquipoPage />} />
          <Route path={ResumenPage.path} element={<ResumenPage />} />
          <Route path={InformeMensual.path} element={<InformeMensual />} />
          <Route path={SinAsignarPage.path} element={<SinAsignarPage />} />
          <Route
            path={ColaboracionesPage.path}
            element={<ColaboracionesPage />}
          />
          <Route path={ComisionesPage.path} element={<ComisionesPage />} />
          <Route
            path={MisComisionesPage.path}
            element={<MisComisionesPage />}
          />
        </CustomRoutes>
        <Resource name="negocios" {...negocios} />
        <Resource name="notas" />
        <Resource name="avisos" />
        <Resource name="cobros" />
        <Resource name="sales" recordRepresentation={representarUsuario} />
      </Admin>
    </PersistQueryClientProvider>
  );
};
